const PLACEHOLDER_STRINGS = new Set([
  'n/a',
  'na',
  'none',
  'null',
  'unknown',
  'not available',
  'not provided',
  'not specified',
  'tbd',
  'to be determined',
  'check eligibility requirements',
  'coming soon',
  'no details available',
  'no keywords available',
  'no description available',
  '(coming soon)',
  '[object object]',
]);

const SCRAPED_FIELD_MAP = {
  Name: 'Name',
  Deadline: 'Deadline',
  Amount: 'Amount',
  pSuccess: 'pSuccess',
  TimeEstimateToComplete: 'TimeEstimateToComplete',
  Cost: 'Cost',
  GrantUrl: 'GrantUrl',
  Eligible_Industry: 'Eligible_Industry',
  Eligible_Region: 'Eligible_Region',
  Eligible_Employees: 'Eligible_Employees',
  Eligible_Founded: 'Eligible_Founded',
  Eligible_Revenue: 'Eligible_Revenue',
  Description_2: 'Description_2',
  Eligibility_Criteria: 'Eligibility_Criteria',
  Eligible_Activities: 'Eligible_Activities',
  Eligible_Applicants: 'Eligible_Applicants',
  ApplicationGuide: 'ApplicationGuide',
  Deadline_To_Apply: 'Deadline_To_Apply',
  Grant_Goal: 'Grant_Goal',
  Description: 'Description',
  Category: 'Category',
  Eligible_Profitable: 'Eligible_Profitable',
  GrantOrLoan: 'GrantOrLoan',
  OpenOrClosed: 'OpenOrClosed',
};

export function normalizeGrantFieldText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  if (typeof value === 'object') {
    if (typeof value.value === 'string') return value.value.trim();
    if (value.value != null && typeof value.value !== 'object') {
      return String(value.value).trim();
    }
    return '';
  }
  return String(value).trim();
}

export function isGrantFieldEmpty(value) {
  const text = normalizeGrantFieldText(value);
  if (!text) return true;
  return PLACEHOLDER_STRINGS.has(text.toLowerCase().replace(/\s+/g, ' '));
}

export function pickGrantFieldValue(...candidates) {
  for (const candidate of candidates) {
    const text = normalizeGrantFieldText(candidate);
    if (text && !isGrantFieldEmpty(text)) {
      return text;
    }
  }
  return null;
}

/**
 * Return only meaningful incoming values. Useful when a verified source should
 * replace research output without copying blanks or placeholder text.
 */
export function meaningfulGrantFields(fields = {}) {
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => !isGrantFieldEmpty(value))
  );
}

/**
 * Fill missing/placeholder fields without overwriting established production data.
 */
export function mergeGrantFieldsFillEmpty(existingFields = {}, incomingFields = {}) {
  const patch = {};
  for (const [field, value] of Object.entries(incomingFields)) {
    if (isGrantFieldEmpty(existingFields[field]) && !isGrantFieldEmpty(value)) {
      patch[field] = value;
    }
  }
  return patch;
}

function wasMeaningfullyExtracted(dataDict, dictKey) {
  if (!dataDict || !(dictKey in dataDict)) return false;
  const raw = dataDict[dictKey];
  if (raw == null || raw === '') return false;
  if (typeof raw === 'string' && isGrantFieldEmpty(raw)) return false;
  return true;
}

function shouldApplyNumericField(dictKey, dataDict, existingFields, airtableField, scrapedValue) {
  if (!wasMeaningfullyExtracted(dataDict, dictKey)) return false;

  if (dictKey === 'Amount') {
    const parsed = parseFloat(dataDict.Amount);
    if (Number.isNaN(parsed) || parsed === 0) {
      return isGrantFieldEmpty(existingFields[airtableField]);
    }
  }

  return !isGrantFieldEmpty(scrapedValue);
}

export function buildGrantTextBlobFromScrape(dataDict) {
  const parts = [dataDict?.Description_2, dataDict?.Description, dataDict?.Grant_Goal]
    .map(normalizeGrantFieldText)
    .filter((part) => part && !isGrantFieldEmpty(part));

  return parts.length ? parts.join('\n\n') : null;
}

export function buildGrantKeywordsFromScrape(dataDict) {
  const parts = [dataDict?.Category, dataDict?.Eligible_Industry, dataDict?.GrantOrLoan]
    .map(normalizeGrantFieldText)
    .filter((part) => part && !isGrantFieldEmpty(part));

  return parts.length ? parts.join(', ') : null;
}

export const GRANT_REFRESH_MODES = {
  FILL: 'fill',
  REPLACE: 'replace',
};

function applyScrapedFieldPatch(patch, airtableField, dictKey, dataDict, existingFields, scrapedValue, options = {}) {
  const { overwriteExisting = false } = options;
  const numericFields = new Set(['Amount', 'pSuccess', 'TimeEstimateToComplete', 'Cost']);
  const shouldApply = numericFields.has(airtableField)
    ? shouldApplyNumericField(dictKey, dataDict, existingFields, airtableField, scrapedValue)
    : wasMeaningfullyExtracted(dataDict, dictKey) && !isGrantFieldEmpty(scrapedValue);

  if (!shouldApply) return;

  if (overwriteExisting || isGrantFieldEmpty(existingFields[airtableField])) {
    patch[airtableField] = scrapedValue;
  }
}

function buildScrapedFieldPatch(existingFields = {}, scrapedFields = {}, dataDict = {}, { overwriteExisting = false } = {}) {
  const patch = {};

  for (const [airtableField, dictKey] of Object.entries(SCRAPED_FIELD_MAP)) {
    applyScrapedFieldPatch(
      patch,
      airtableField,
      dictKey,
      dataDict,
      existingFields,
      scrapedFields[airtableField],
      { overwriteExisting }
    );
  }

  const blobText = buildGrantTextBlobFromScrape(dataDict);
  if (blobText && (overwriteExisting || (
    isGrantFieldEmpty(existingFields.GrantTextBlobUpdate) &&
    isGrantFieldEmpty(existingFields.GrantTextBlob)
  ))) {
    patch.GrantTextBlobUpdate = blobText;
  }

  const keywords = buildGrantKeywordsFromScrape(dataDict);
  if (keywords && (overwriteExisting || isGrantFieldEmpty(existingFields.GrantKeywords))) {
    patch.GrantKeywords = keywords;
  }

  return patch;
}

/**
 * Fill empty/placeholder fields with scraped values; never overwrite good existing data.
 */
export function mergeScrapedGrantFields(existingFields = {}, scrapedFields = {}, dataDict = {}) {
  return buildScrapedFieldPatch(existingFields, scrapedFields, dataDict, { overwriteExisting: false });
}

/**
 * Replace scraped grant fields from a fresh scrape (e.g. after fixing a wrong URL).
 */
export function replaceScrapedGrantFields(existingFields = {}, scrapedFields = {}, dataDict = {}) {
  return buildScrapedFieldPatch(existingFields, scrapedFields, dataDict, { overwriteExisting: true });
}

export function getGrantSummaryText(fields) {
  return (
    pickGrantFieldValue(fields?.Subtitle, fields?.Description_2, fields?.Description) ||
    'No description available'
  );
}

export function getGrantDetailsText(fields) {
  return pickGrantFieldValue(fields?.GrantTextBlobUpdate, fields?.GrantTextBlob, fields?.Grant_Goal);
}

export function getGrantKeywordsText(fields) {
  const explicit = pickGrantFieldValue(fields?.GrantKeywords);
  if (explicit) return explicit;

  const derived = [fields?.Category, fields?.Eligible_Industry, fields?.GrantOrLoan]
    .map(normalizeGrantFieldText)
    .filter((part) => part && !isGrantFieldEmpty(part));

  return derived.length ? derived.join(', ') : null;
}
